import React, { useMemo, useState } from 'react';
import { Tabs } from 'antd';
import termAcceptanceApi from '../../services/termAcceptanceApi';
import Notification from '../../components/notification/notification';
import { TWButton } from '../../components/ui';

const MerchantAgreement = ({ merchantTerms = [], onAccepted }) => {
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showNotf, setShowNotf] = useState(false);
  const [notfMessage, setNotfMessage] = useState('');

  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );

  const hasMultipleCompanies = merchantTerms.length > 1;

  const handleAccept = async () => {
    if (!accepted || merchantTerms.length === 0) return;
    setAcceptLoading(true);
    setShowNotf(false);
    try {
      const items = merchantTerms.map((item) => ({
        termId: item.termId,
        companyId: item.companyId || undefined,
        merchantTermHtml: item.merchantTermHtml || undefined,
      }));
      await termAcceptanceApi.bulkCreate({ timeZone, items });
      onAccepted();
    } catch (err) {
      setNotfMessage(
        err?.response?.data?.message ||
          err?.response?.data ||
          'Failed to record acceptance. Please try again.',
      );
      setShowNotf(true);
    } finally {
      setAcceptLoading(false);
    }
  };

  if (!merchantTerms.length) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center rounded-md border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p className="text-center text-slate-600 dark:text-slate-300">
          Merchant agreement is not available.
        </p>
      </div>
    );
  }

  const renderHtmlContent = (html) => {
    if (!html) {
      return (
        <p className="text-slate-500 dark:text-slate-400">
          No content available.
        </p>
      );
    }
    return (
      <div
        className="prose prose-sm max-w-none dark:prose-invert"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const tabItems = merchantTerms.map((item, index) => ({
    key: item.companyId || `term-${index}`,
    label: item.companyName || 'Merchant Agreement',
    children: (
      <div className="max-h-[50vh] overflow-y-auto">
        {renderHtmlContent(item.merchantTermHtml)}
      </div>
    ),
  }));

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="border-b border-slate-200 bg-slate-50/60 px-6 py-5 dark:border-slate-700 dark:bg-slate-900/40">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Merchant Agreement
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Please review and accept the agreement to continue.
          </p>
        </div>

        <div className="px-6 py-5">
          {hasMultipleCompanies ? (
            <Tabs items={tabItems} />
          ) : (
            <div className="max-h-[55vh] overflow-y-auto">
              {renderHtmlContent(merchantTerms[0]?.merchantTermHtml)}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50/60 px-6 py-5 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-slate-500 dark:bg-slate-700"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                I have read and agree to the Thisway Merchant Agreement
                (including all Addenda) and I represent that I am authorized to
                bind the Merchant to these terms.
              </span>
            </label>
            <TWButton
              size="lg"
              label="Accept and Continue"
              disabled={!accepted}
              loading={acceptLoading}
              onClick={handleAccept}
              className={
                accepted
                  ? '!border-emerald-600 !bg-emerald-600 !text-white hover:!bg-emerald-700'
                  : ''
              }
            />
          </div>
        </div>
      </div>
      <Notification
        display={showNotf}
        type="error"
        message={notfMessage}
        onClose={() => setShowNotf(false)}
      />
    </div>
  );
};

export default MerchantAgreement;
